import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { dashboardService } from '@/services/dashboard.service';
import type {
  DashboardStats,
  ActivityItem,
  UpcomingEvent,
  UpcomingBirthday,
  QuickStats,
} from '@/services/dashboard.service';
import { ApiError } from '@/services/api';

// Dashboard state interface
interface DashboardState {
  stats: DashboardStats | null;
  activity: ActivityItem[];
  upcomingEvents: UpcomingEvent[];
  upcomingBirthdays: UpcomingBirthday[];
  quickStats: QuickStats | null;
  isLoading: boolean;
  isLoadingStats: boolean;
  isLoadingActivity: boolean;
  isLoadingEvents: boolean;
  isLoadingBirthdays: boolean;
  isLoadingQuickStats: boolean;
  error: string | null;
  lastUpdated: string | null;
}

// Initial state
const initialState: DashboardState = {
  stats: null,
  activity: [],
  upcomingEvents: [],
  upcomingBirthdays: [],
  quickStats: null,
  isLoading: false,
  isLoadingStats: false,
  isLoadingActivity: false,
  isLoadingEvents: false,
  isLoadingBirthdays: false,
  isLoadingQuickStats: false,
  error: null,
  lastUpdated: null,
};

// Async thunks
export const fetchDashboardStats = createAsyncThunk<
  DashboardStats,
  void,
  { rejectValue: string }
>('dashboard/fetchStats', async (_, { rejectWithValue }) => {
  try {
    return await dashboardService.getStats();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Dashboard-Statistiken');
  }
});

export const fetchDashboardActivity = createAsyncThunk<
  ActivityItem[],
  number | undefined,
  { rejectValue: string }
>('dashboard/fetchActivity', async (limit = 10, { rejectWithValue }) => {
  try {
    return await dashboardService.getActivity(limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Aktivitaeten');
  }
});

export const fetchUpcomingEvents = createAsyncThunk<
  UpcomingEvent[],
  number | undefined,
  { rejectValue: string }
>('dashboard/fetchUpcomingEvents', async (limit = 5, { rejectWithValue }) => {
  try {
    return await dashboardService.getUpcomingEvents(limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Events');
  }
});

export const fetchUpcomingBirthdays = createAsyncThunk<
  UpcomingBirthday[],
  number | undefined,
  { rejectValue: string }
>('dashboard/fetchUpcomingBirthdays', async (limit = 5, { rejectWithValue }) => {
  try {
    return await dashboardService.getUpcomingBirthdays(limit);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Geburtstage');
  }
});

export const fetchQuickStats = createAsyncThunk<
  QuickStats,
  void,
  { rejectValue: string }
>('dashboard/fetchQuickStats', async (_, { rejectWithValue }) => {
  try {
    return await dashboardService.getQuickStats();
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Quick Stats');
  }
});

// Fetch all dashboard data at once
export const fetchAllDashboardData = createAsyncThunk<
  void,
  void,
  { rejectValue: string }
>('dashboard/fetchAll', async (_, { dispatch, rejectWithValue }) => {
  try {
    await Promise.all([
      dispatch(fetchDashboardStats()),
      dispatch(fetchDashboardActivity()),
      dispatch(fetchUpcomingEvents()),
      dispatch(fetchUpcomingBirthdays()),
      dispatch(fetchQuickStats()),
    ]);
  } catch (error) {
    if (error instanceof ApiError) {
      return rejectWithValue(error.message);
    }
    return rejectWithValue('Fehler beim Laden der Dashboard-Daten');
  }
});

// Dashboard slice
const dashboardSlice = createSlice({
  name: 'dashboard',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    resetDashboard: () => initialState,
  },
  extraReducers: (builder) => {
    // Fetch Stats
    builder
      .addCase(fetchDashboardStats.pending, (state) => {
        state.isLoadingStats = true;
        state.error = null;
      })
      .addCase(fetchDashboardStats.fulfilled, (state, action) => {
        state.isLoadingStats = false;
        state.stats = action.payload;
        state.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchDashboardStats.rejected, (state, action) => {
        state.isLoadingStats = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch Activity
    builder
      .addCase(fetchDashboardActivity.pending, (state) => {
        state.isLoadingActivity = true;
        state.error = null;
      })
      .addCase(fetchDashboardActivity.fulfilled, (state, action) => {
        state.isLoadingActivity = false;
        state.activity = action.payload;
      })
      .addCase(fetchDashboardActivity.rejected, (state, action) => {
        state.isLoadingActivity = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch Upcoming Events
    builder
      .addCase(fetchUpcomingEvents.pending, (state) => {
        state.isLoadingEvents = true;
        state.error = null;
      })
      .addCase(fetchUpcomingEvents.fulfilled, (state, action) => {
        state.isLoadingEvents = false;
        state.upcomingEvents = action.payload;
      })
      .addCase(fetchUpcomingEvents.rejected, (state, action) => {
        state.isLoadingEvents = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch Upcoming Birthdays
    builder
      .addCase(fetchUpcomingBirthdays.pending, (state) => {
        state.isLoadingBirthdays = true;
        state.error = null;
      })
      .addCase(fetchUpcomingBirthdays.fulfilled, (state, action) => {
        state.isLoadingBirthdays = false;
        state.upcomingBirthdays = action.payload;
      })
      .addCase(fetchUpcomingBirthdays.rejected, (state, action) => {
        state.isLoadingBirthdays = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch Quick Stats
    builder
      .addCase(fetchQuickStats.pending, (state) => {
        state.isLoadingQuickStats = true;
        state.error = null;
      })
      .addCase(fetchQuickStats.fulfilled, (state, action) => {
        state.isLoadingQuickStats = false;
        state.quickStats = action.payload;
      })
      .addCase(fetchQuickStats.rejected, (state, action) => {
        state.isLoadingQuickStats = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });

    // Fetch All
    builder
      .addCase(fetchAllDashboardData.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAllDashboardData.fulfilled, (state) => {
        state.isLoading = false;
      })
      .addCase(fetchAllDashboardData.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? 'Fehler beim Laden';
      });
  },
});

// Export actions
export const { clearError, resetDashboard } = dashboardSlice.actions;

// Selectors
export const selectDashboard = (state: { dashboard: DashboardState }) => state.dashboard;
export const selectDashboardStats = (state: { dashboard: DashboardState }) => state.dashboard.stats;
export const selectDashboardActivity = (state: { dashboard: DashboardState }) => state.dashboard.activity;
export const selectUpcomingEvents = (state: { dashboard: DashboardState }) => state.dashboard.upcomingEvents;
export const selectUpcomingBirthdays = (state: { dashboard: DashboardState }) => state.dashboard.upcomingBirthdays;
export const selectQuickStats = (state: { dashboard: DashboardState }) => state.dashboard.quickStats;
export const selectDashboardLoading = (state: { dashboard: DashboardState }) => ({
  isLoading: state.dashboard.isLoading,
  isLoadingStats: state.dashboard.isLoadingStats,
  isLoadingActivity: state.dashboard.isLoadingActivity,
  isLoadingEvents: state.dashboard.isLoadingEvents,
  isLoadingBirthdays: state.dashboard.isLoadingBirthdays,
  isLoadingQuickStats: state.dashboard.isLoadingQuickStats,
});
export const selectDashboardError = (state: { dashboard: DashboardState }) => state.dashboard.error;

// Export reducer
export default dashboardSlice.reducer;
